import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

export type PanelId = 'search' | 'explore' | 'locate' | 'layers';

/**
 * Chrome state: which panel is open, what the catalog is filtered to, and
 * whether the side drawer is showing on small screens.
 *
 * The three panel ids mirror the tabs long-time OKMaps users already know
 * (Search / Explore / Locate), plus a Layers panel for the active map stack.
 * Familiar navigation was an explicit product requirement.
 */
export interface UiState {
  readonly panel: PanelId;
  readonly drawerOpen: boolean;
  readonly searchText: string;
  readonly category: string | null;
  readonly renderableOnly: boolean;
  readonly colorMode: 'light' | 'dark';
  /** Record whose metadata dialog is open, if any. */
  readonly metadataFor: string | null;
}

/**
 * The panel starts open on anything wider than a phone and closed below that.
 *
 * On a 390px screen an open panel covers the map completely, so the first thing
 * a visitor sees is a list with no indication that a map exists. Starting
 * closed means the map is the landing view and the panel is one tap away, which
 * is the right order for a mapping application.
 */
const PANEL_OPEN_MIN_WIDTH = 900;

const initialState: UiState = {
  panel: 'explore',
  drawerOpen:
    typeof window === 'undefined' ? true : window.innerWidth >= PANEL_OPEN_MIN_WIDTH,
  searchText: '',
  category: null,
  renderableOnly: false,
  colorMode: 'light',
  metadataFor: null,
};

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    panelChanged(state, action: PayloadAction<PanelId>) {
      state.panel = action.payload;
      state.drawerOpen = true;
    },
    drawerToggled(state) {
      state.drawerOpen = !state.drawerOpen;
    },
    drawerSet(state, action: PayloadAction<boolean>) {
      state.drawerOpen = action.payload;
    },
    searchTextChanged(state, action: PayloadAction<string>) {
      state.searchText = action.payload;
    },
    categorySelected(state, action: PayloadAction<string | null>) {
      state.category = action.payload;
    },
    renderableOnlyToggled(state) {
      state.renderableOnly = !state.renderableOnly;
    },
    colorModeToggled(state) {
      state.colorMode = state.colorMode === 'light' ? 'dark' : 'light';
    },
    metadataOpened(state, action: PayloadAction<string>) {
      state.metadataFor = action.payload;
    },
    metadataClosed(state) {
      state.metadataFor = null;
    },
  },
});

export const {
  panelChanged,
  drawerToggled,
  drawerSet,
  searchTextChanged,
  categorySelected,
  renderableOnlyToggled,
  colorModeToggled,
  metadataOpened,
  metadataClosed,
} = uiSlice.actions;

export default uiSlice.reducer;
